/**
 * Normalize pasted / flat contract HTML into nested-numbering layout:
 * main section heads bold, body clauses hanging-indented by depth,
 * empty spacer paragraphs removed.
 */

import { looksLikeLegalHeading, promotePlainHeadings, stripTags } from '@/lib/contract-clauses';

const NUMBERED_LINE =
  /^(\d+(?:\.\d+)*)(?:[.)]\s+|\s+)([\s\S]+)$/;

export type ContractLanguageLineKind = 'heading' | 'body' | null;

/** Classify a plain-text line for contract numbering layout. */
export function classifyContractLanguageLine(text: string): {
  kind: ContractLanguageLineKind;
  nestDepth: number;
  prefix: string;
} {
  const trimmed = text.trim();
  if (!trimmed) return { kind: null, nestDepth: 0, prefix: '' };

  const m = trimmed.match(NUMBERED_LINE);
  if (!m) {
    if (looksLikeLegalHeading(trimmed) && !/^\d/.test(trimmed)) {
      return { kind: 'heading', nestDepth: 0, prefix: '' };
    }
    return { kind: null, nestDepth: 0, prefix: '' };
  }

  const prefix = m[1];
  const rest = m[2].trim();
  const parts = prefix.split('.');

  // ACORD-style section heads: "1.0 Scope of Work…"
  if (parts.length === 2 && parts[1] === '0') {
    return { kind: 'heading', nestDepth: 0, prefix };
  }

  // Single-number short titles: "1. RIGHT OF CANCELLATION"
  if (parts.length === 1) {
    const titleLike =
      rest.length <= 90 &&
      !/[.?!].*\S/.test(rest.slice(0, -1)) &&
      /^[A-Z0-9]/.test(rest);
    if (titleLike || looksLikeLegalHeading(trimmed)) {
      return { kind: 'heading', nestDepth: 0, prefix };
    }
  }

  // Nested body: 1.1 / 1.1.1 / …
  return {
    kind: 'body',
    nestDepth: Math.max(1, parts.length - 1),
    prefix,
  };
}

function stripEmptyParagraphs(html: string): string {
  return html
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/(<br\s*\/?>\s*){2,}/gi, '<br />');
}

function ensureHeadingBold(inner: string): string {
  const text = stripTags(inner);
  if (!text) return inner;
  if (/<(?:strong|b)\b/i.test(inner)) return inner;
  return `<strong>${inner}</strong>`;
}

/**
 * Rewrite contract HTML so main numbered headings are bold `<h3>`s and
 * nested numbered paragraphs get `cd-cl-n{depth}` for hanging indents.
 * Idempotent for already-formatted markup.
 */
export function formatContractLanguageHtml(html: string | null | undefined): string {
  const raw = (html ?? '').trim();
  if (!raw) return '';

  let out = promotePlainHeadings(raw);
  out = stripEmptyParagraphs(out);

  // Existing headings — force bold content, mark for CSS.
  out = out.replace(/<h([1-6])(\b[^>]*)>([\s\S]*?)<\/h\1>/gi, (_, level, attrs, inner) => {
    const cleanAttrs = String(attrs || '')
      .replace(/\s*class="[^"]*"/i, '')
      .trim();
    const classAttr = ' class="cd-cl-h"';
    return `<h${level}${cleanAttrs ? ` ${cleanAttrs}` : ''}${classAttr}>${ensureHeadingBold(inner)}</h${level}>`;
  });

  // Paragraphs — promote main heads; indent nested numbered bodies.
  out = out.replace(/<p(\b[^>]*)>([\s\S]*?)<\/p>/gi, (_full, attrs, inner) => {
    const text = stripTags(inner);
    const { kind, nestDepth } = classifyContractLanguageLine(text);
    if (kind === 'heading') {
      return `<h3 class="cd-cl-h">${ensureHeadingBold(inner)}</h3>`;
    }
    if (kind === 'body') {
      const depth = Math.min(nestDepth, 4);
      const cleanAttrs = String(attrs || '')
        .replace(/\s*class="[^"]*"/i, '')
        .trim();
      return `<p${cleanAttrs ? ` ${cleanAttrs}` : ''} class="cd-cl-n${depth}">${inner}</p>`;
    }
    // Intro / unnumbered — drop accidental nest classes, keep tight.
    const cleanAttrs = String(attrs || '')
      .replace(/\s*class="[^"]*"/i, '')
      .trim();
    return `<p${cleanAttrs ? ` ${cleanAttrs}` : ''} class="cd-cl-intro">${inner}</p>`;
  });

  return stripEmptyParagraphs(out);
}
